import React from "react";

/**
 * TechniqueSchematic
 * ──────────────────
 * Trans-affirming face schematic that shows BOTH:
 *   (a) WHERE to apply each makeup product (soft zone halo)
 *   (b) HOW to apply it — the specific technique:
 *       direction arrows, stipple-pat dots, wiggle motion,
 *       circular swirls, sweep curves, hair-strokes, etc.
 *
 * Each category returns its own technique drawing + a short caption
 * that describes the motion in plain language (e.g. "Trace a soft 3 —
 * temple → cheek → jaw"). Caption is rendered by the parent.
 *
 * No specific face/skin/jawline is imposed on the user — soft, neutral.
 */

// ── Drawing constants ─────────────────────────────────────────────────────
const VB_W = 200;
const VB_H = 240;

const COLORS = {
  faceFill: "#FAEFE9",
  faceLine: "#D5BBB6",
  hair: "#E8D6CD",
  innerLine: "#C9A8A4",
  zone: "#9B6A6C",
  zoneSoft: "#D5A8A4",
  arrow: "#5D4050",
  tap: "#9B6A6C",
};

// ── Reusable motion primitives ────────────────────────────────────────────
const Halo = ({ d, opacity = 0.55 }) => (
  <>
    <path d={d} fill={COLORS.zoneSoft} opacity={opacity * 0.55} filter="url(#zone-blur)" />
    <path d={d} fill={COLORS.zone} opacity={opacity * 0.4} />
    <path d={d} fill="none" stroke={COLORS.zone} strokeWidth="1.1" strokeDasharray="0.6 2" opacity={opacity} />
  </>
);

const HaloCircle = ({ cx, cy, r, opacity = 0.55 }) => (
  <>
    <circle cx={cx} cy={cy} r={r + 2} fill={COLORS.zoneSoft} opacity={opacity * 0.5} filter="url(#zone-blur)" />
    <circle cx={cx} cy={cy} r={r} fill={COLORS.zone} opacity={opacity * 0.45} />
  </>
);

// Straight directional arrow
const Arrow = ({ from, to, width = 1.4, opacity = 0.95 }) => (
  <line
    x1={from[0]} y1={from[1]} x2={to[0]} y2={to[1]}
    stroke={COLORS.arrow}
    strokeWidth={width}
    strokeLinecap="round"
    markerEnd="url(#arrow-head)"
    opacity={opacity}
  />
);

// Curved arrow using a quadratic bezier — control point steers the curve
const CurveArrow = ({ from, ctrl, to, width = 1.4, opacity = 0.95, dashed = false }) => (
  <path
    d={`M${from[0]},${from[1]} Q${ctrl[0]},${ctrl[1]} ${to[0]},${to[1]}`}
    fill="none"
    stroke={COLORS.arrow}
    strokeWidth={width}
    strokeLinecap="round"
    strokeDasharray={dashed ? "2 2" : ""}
    markerEnd="url(#arrow-head)"
    opacity={opacity}
  />
);

// "Tap / pat / stipple" indicator — a small dot ring
const TapDot = ({ cx, cy, r = 1.2, opacity = 1 }) => (
  <g opacity={opacity}>
    <circle cx={cx} cy={cy} r={r + 1.4} fill={COLORS.tap} opacity={0.18} />
    <circle cx={cx} cy={cy} r={r} fill={COLORS.tap} />
  </g>
);

// Wiggle path (mascara) — zigzag from base to tip
const Wiggle = ({ from, to, amp = 1.2, segs = 5, width = 1.2 }) => {
  const dx = (to[0] - from[0]) / segs;
  const dy = (to[1] - from[1]) / segs;
  let d = `M${from[0]},${from[1]}`;
  for (let i = 1; i <= segs; i++) {
    const sx = from[0] + dx * i;
    const sy = from[1] + dy * i;
    const offset = i % 2 === 0 ? -amp : amp;
    // wiggle perpendicular-ish on x for vertical strokes
    d += ` L${sx + offset},${sy}`;
  }
  return (
    <path d={d} fill="none" stroke={COLORS.zone} strokeWidth={width} strokeLinecap="round" />
  );
};

// Hair stroke — short angled line (used for brows)
const HairStroke = ({ from, to }) => (
  <line
    x1={from[0]} y1={from[1]} x2={to[0]} y2={to[1]}
    stroke={COLORS.zone} strokeWidth="1.2" strokeLinecap="round" opacity="1"
  />
);

// Spiral / circular motion (blush)
const Swirl = ({ cx, cy, r = 6 }) => (
  <path
    d={`M ${cx + r},${cy}
        A ${r},${r} 0 1 1 ${cx - r * 0.2},${cy - r * 0.95}
        A ${r * 0.75},${r * 0.75} 0 1 0 ${cx + r * 0.55},${cy - r * 0.2}`}
    fill="none"
    stroke={COLORS.arrow}
    strokeWidth="1.2"
    strokeLinecap="round"
    markerEnd="url(#arrow-head)"
    opacity="0.95"
  />
);

// Sparkle (highlight tap)
const Sparkle = ({ cx, cy, size = 3 }) => (
  <g opacity="0.95">
    <line x1={cx - size} y1={cy} x2={cx + size} y2={cy} stroke={COLORS.zone} strokeWidth="0.9" strokeLinecap="round" />
    <line x1={cx} y1={cy - size} x2={cx} y2={cy + size} stroke={COLORS.zone} strokeWidth="0.9" strokeLinecap="round" />
    <line x1={cx - size * 0.7} y1={cy - size * 0.7} x2={cx + size * 0.7} y2={cy + size * 0.7} stroke={COLORS.zone} strokeWidth="0.6" strokeLinecap="round" opacity="0.7" />
  </g>
);

// ── Generic face outline ──────────────────────────────────────────────────
const FaceBase = () => (
  <g>
    <path
      d="M40,72
         C40,32 78,12 100,12
         C122,12 160,32 160,72
         C170,80 168,108 158,118
         C156,90 152,76 146,68
         C146,70 144,76 140,82
         C124,72 110,68 100,68
         C90,68 76,72 60,82
         C56,76 54,70 54,68
         C48,76 44,90 42,118
         C32,108 30,80 40,72 Z"
      fill={COLORS.hair} opacity="0.7"
    />
    <path
      d="M58,90
         C58,72 76,60 100,60
         C124,60 142,72 142,90
         C146,108 142,134 132,158
         C124,180 114,196 100,202
         C86,196 76,180 68,158
         C58,134 54,108 58,90 Z"
      fill={COLORS.faceFill} stroke={COLORS.faceLine} strokeWidth="0.8"
    />
    {/* Brows */}
    <path d="M72,108 Q82,102 94,108" fill="none" stroke={COLORS.innerLine} strokeWidth="2" strokeLinecap="round" />
    <path d="M106,108 Q118,102 128,108" fill="none" stroke={COLORS.innerLine} strokeWidth="2" strokeLinecap="round" />
    {/* Eyes */}
    <path d="M76,118 Q82,114 90,118" fill="none" stroke={COLORS.innerLine} strokeWidth="1.2" strokeLinecap="round" />
    <path d="M110,118 Q118,114 124,118" fill="none" stroke={COLORS.innerLine} strokeWidth="1.2" strokeLinecap="round" />
    {/* Nose */}
    <path d="M100,124 L97,144 Q100,148 103,144" fill="none" stroke={COLORS.innerLine} strokeWidth="1" strokeLinecap="round" />
    {/* Mouth */}
    <path d="M88,170 Q100,166 112,170 Q100,174 88,170 Z" fill="#E2B3AE" opacity="0.7" />
    <path d="M88,170 Q100,166 112,170" fill="none" stroke={COLORS.innerLine} strokeWidth="0.8" />
  </g>
);

// Full-face oval shape (reused)
const FACE_PATH = "M58,90 C58,72 76,60 100,60 C124,60 142,72 142,90 C146,108 142,134 132,158 C124,180 114,196 100,202 C86,196 76,180 68,158 C58,134 54,108 58,90 Z";

// ── Technique definitions ────────────────────────────────────────────────
// Each entry returns: { caption, render() }
const TECHNIQUES = {
  primer: {
    caption: "Press & pat outward — start at the center, work to the edges",
    tool: "Fingertips or damp sponge",
    steps: [
      "Squeeze a pea-sized amount onto the back of your hand",
      "Dot it on forehead, nose, both cheeks, and chin",
      "Press & pat outward — never rub or drag",
      "Let it sink in for 30 seconds before foundation",
    ],
    render: () => (
      <>
        <Halo d={FACE_PATH} opacity={0.35} />
        {/* Stipple/pat dots scattered outward */}
        {[
          [100, 80], [86, 86], [114, 86], [78, 100], [122, 100],
          [70, 118], [130, 118], [80, 138], [120, 138],
          [88, 156], [112, 156], [100, 175],
        ].map(([x, y]) => <TapDot key={`primer-${x}-${y}`} cx={x} cy={y} r={1} />)}
        {/* Outward press direction */}
        <Arrow from={[100, 96]} to={[80, 96]} width={1} opacity={0.7} />
        <Arrow from={[100, 96]} to={[120, 96]} width={1} opacity={0.7} />
        <Arrow from={[100, 140]} to={[78, 144]} width={1} opacity={0.7} />
        <Arrow from={[100, 140]} to={[122, 144]} width={1} opacity={0.7} />
      </>
    ),
  },

  foundation: {
    caption: "Stipple from the center, then blend outward — never drag",
    tool: "Damp beauty sponge or stippling brush",
    steps: [
      "Dot foundation on the center of your face (forehead, nose, cheeks, chin)",
      "Stipple — bounce the sponge or brush, don't sweep",
      "Work outward toward the hairline and jaw, fading at the edges",
      "Build a second layer only where you want more coverage",
    ],
    render: () => (
      <>
        <Halo d={FACE_PATH} opacity={0.42} />
        {/* Radiating outward arrows */}
        <Arrow from={[100, 100]} to={[78, 92]} width={1.1} />
        <Arrow from={[100, 100]} to={[122, 92]} width={1.1} />
        <Arrow from={[100, 130]} to={[72, 134]} width={1.1} />
        <Arrow from={[100, 130]} to={[128, 134]} width={1.1} />
        <Arrow from={[100, 160]} to={[80, 172]} width={1.1} />
        <Arrow from={[100, 160]} to={[120, 172]} width={1.1} />
        {/* Stipple dots */}
        {[[100, 88], [100, 116], [100, 146], [100, 176], [80, 110], [120, 110]].map(
          ([x, y]) => <TapDot key={`fnd-${x}-${y}`} cx={x} cy={y} r={1.1} />
        )}
      </>
    ),
  },

  concealer: {
    caption:
      "Place three tiny dots beneath each eye, then tap from the inner corner outward",
    tool: "Fingertip or small fluffy concealer brush",
    steps: [
      "Tap a brightening concealer one shade lighter than your foundation",
      "Place three tiny dots under each eye — inner corner, middle, outer (no streaks)",
      "Pat — never rub — until each dot melts into the skin",
      "Set under the eyes with a whisper of translucent powder so it doesn't crease",
    ],
    render: () => (
      <>
        {/* Three tap points per eye — inner, middle, outer — matching
            the live mirror's tracked sparkle treatment. */}
        {[
          [86, 116], [90, 120], [94, 118],   // right under-eye
          [114, 116], [110, 120], [106, 118],// left under-eye
        ].map(([x, y]) => (
          <TapDot key={`con-${x}-${y}`} cx={x} cy={y} r={1.2} />
        ))}
      </>
    ),
  },

  color_corrector: {
    caption:
      "Press a peach/orange corrector across the lower face — wherever shadow shows up. Never rub or sweep.",
    tool: "Small concealer brush or fingertip",
    steps: [
      "Pick a corrector one shade more peach/orange than your skin",
      "Press a thin layer across your lower face — chin, jaw, mustache, anywhere shadow lives",
      "Tap, never sweep — sweeping lifts it off your skin",
      "Wait 20 seconds before foundation so it sets",
    ],
    render: () => (
      <>
        {/* Wide lower-face halo — covers cheeks, mustache, jaw, chin
            (matches the AR mirror's expanded color-corrector zone). */}
        <Halo
          d="M64,128 Q70,150 78,168 Q86,186 100,198 Q114,186 122,168 Q130,150 136,128 Q124,140 100,142 Q76,140 64,128 Z"
        />
        <Halo
          d="M72,140 Q80,158 92,170 Q100,178 108,170 Q120,158 128,140 Q116,148 100,150 Q84,148 72,140 Z"
          opacity={0.35}
        />
        {/* Pat dots distributed across the lower face — both cheeks,
            philtrum/mustache, both jowls, chin — showing the user
            where to tap, not stroke. */}
        {[
          // Right cheek + jowl
          [78, 142], [82, 156], [86, 170], [90, 182],
          // Left cheek + jowl
          [122, 142], [118, 156], [114, 170], [110, 182],
          // Mustache / philtrum
          [94, 144], [100, 142], [106, 144],
          // Chin
          [96, 188], [100, 192], [104, 188],
        ].map(([x, y]) => <TapDot key={`cc-${x}-${y}`} cx={x} cy={y} r={1.3} />)}
      </>
    ),
  },

  contour: {
    caption: "Sweep diagonally from temple → toward mouth corner, then blend up & out",
    tool: "Angled contour brush",
    steps: [
      "Suck in your cheeks lightly to find the natural hollow",
      "Sweep contour in a diagonal line from temple toward the mouth corner",
      "Stop halfway — never bring contour past the pupil",
      "Blend upward and outward in small circles until there's no edge",
    ],
    render: () => (
      <>
        {/* Hollow under cheekbone — left + right */}
        <Halo d="M62,128 Q72,124 86,134 Q82,150 72,150 Q62,144 62,128 Z" opacity={0.6} />
        <Halo d="M138,128 Q128,124 114,134 Q118,150 128,150 Q138,144 138,128 Z" opacity={0.6} />
        {/* Jawline + temple */}
        <Halo d="M58,158 Q70,168 86,170 Q82,180 64,178 Q56,170 58,158 Z" opacity={0.4} />
        <Halo d="M142,158 Q130,168 114,170 Q118,180 136,178 Q144,170 142,158 Z" opacity={0.4} />
        {/* Diagonal sweep arrows — main contour direction */}
        <CurveArrow from={[64, 122]} ctrl={[72, 138]} to={[86, 148]} width={1.6} />
        <CurveArrow from={[136, 122]} ctrl={[128, 138]} to={[114, 148]} width={1.6} />
        {/* Jaw sweep */}
        <CurveArrow from={[64, 162]} ctrl={[74, 174]} to={[86, 178]} width={1.3} dashed />
        <CurveArrow from={[136, 162]} ctrl={[126, 174]} to={[114, 178]} width={1.3} dashed />
      </>
    ),
  },

  bronzer: {
    caption: "Trace a soft '3' on each side: forehead → cheek hollow → jaw",
    tool: "Fluffy powder brush",
    steps: [
      "Tap excess off your brush so the first pass is light",
      "Start at the temple — sweep across the top of the forehead",
      "Curve down through the cheek hollow",
      "End along the jawline — that's the '3' shape",
      "Repeat lightly on the other side; build only if needed",
    ],
    render: () => (
      <>
        {/* Three-zone halo: temple, top of cheek, jaw */}
        <Halo d="M58,84 Q70,76 84,86 Q80,98 70,98 Q58,94 58,84 Z" opacity={0.45} />
        <Halo d="M64,124 Q74,118 86,128 Q82,140 72,140 Q62,134 64,124 Z" opacity={0.45} />
        <Halo d="M62,162 Q74,170 88,168 Q86,180 70,178 Q58,170 62,162 Z" opacity={0.4} />
        <Halo d="M142,84 Q130,76 116,86 Q120,98 130,98 Q142,94 142,84 Z" opacity={0.45} />
        <Halo d="M136,124 Q126,118 114,128 Q118,140 128,140 Q138,134 136,124 Z" opacity={0.45} />
        <Halo d="M138,162 Q126,170 112,168 Q114,180 130,178 Q142,170 138,162 Z" opacity={0.4} />
        {/* The "3" shape — left side */}
        <path
          d="M70,86 Q56,108 76,128 Q92,138 78,158 Q66,168 60,170"
          fill="none" stroke={COLORS.arrow} strokeWidth="1.5"
          strokeLinecap="round" markerEnd="url(#arrow-head)" opacity="0.95"
        />
        {/* The "3" shape — right side (mirrored) */}
        <path
          d="M130,86 Q144,108 124,128 Q108,138 122,158 Q134,168 140,170"
          fill="none" stroke={COLORS.arrow} strokeWidth="1.5"
          strokeLinecap="round" markerEnd="url(#arrow-head)" opacity="0.95"
        />
      </>
    ),
  },

  blush: {
    caption: "Smile, find the apples, place the color there and circle outward towards the temple.",
    tool: "Fluffy blush brush or fingertips for cream",
    steps: [
      "Smile gently — the round apples of your cheeks pop forward",
      "Make small circles right on the apple",
      "Circle outward towards the temple to lift the cheek visually",
      "Tap the brush on your hand and re-blend the edge so it diffuses",
    ],
    render: () => (
      <>
        {/* Circular cheek-apple targets matching the live AR guide. */}
        <Swirl cx={76} cy={138} r={7} />
        <Swirl cx={124} cy={138} r={7} />
        <TapDot cx={76} cy={138} r={1.4} />
        <TapDot cx={124} cy={138} r={1.4} />
      </>
    ),
  },

  highlighter: {
    caption: "Tap (don't sweep) onto cheekbones, brow bone, cupid's bow, nose bridge",
    tool: "Small fan brush or clean fingertip",
    steps: [
      "Find the highest part of the cheekbone (right under the outer eye)",
      "Tap highlighter onto that spot, then the brow bone, cupid's bow, nose tip",
      "Pat — never drag — so the shimmer stays bright and concentrated",
      "Optional: a tiny dot on the chin lifts the whole face",
    ],
    render: () => (
      <>
        <Halo d="M72,128 Q82,124 92,128 Q88,134 80,134 Q72,132 72,128 Z" opacity={0.6} />
        <Halo d="M128,128 Q118,124 108,128 Q112,134 120,134 Q128,132 128,128 Z" opacity={0.6} />
        <Halo d="M97,124 L100,144 L103,124 Z" opacity={0.5} />
        <Halo d="M94,164 Q100,162 106,164 Q100,168 94,164 Z" opacity={0.55} />
        {/* Tap dots — showing pat motion */}
        <TapDot cx={82} cy={130} r={1.4} />
        <TapDot cx={118} cy={130} r={1.4} />
        <TapDot cx={100} cy={134} r={1.3} />
        <TapDot cx={100} cy={166} r={1.3} />
        <TapDot cx={84} cy={104} r={1.2} />
        <TapDot cx={116} cy={104} r={1.2} />
        {/* Sparkles to evoke shimmer */}
        <Sparkle cx={82} cy={130} size={3.2} />
        <Sparkle cx={118} cy={130} size={3.2} />
        <Sparkle cx={100} cy={166} size={2.6} />
        <Sparkle cx={100} cy={132} size={2.4} />
      </>
    ),
  },

  brows: {
    caption: "Brush up, fill if your product fills, then set the shape — works for pomades, pencils, tinted gels, clear gels, and styling waxes",
    tool: "Spoolie + your brow product (pomade, pencil, microfilling pen, tinted gel, clear gel, or styling wax)",
    steps: [
      "Brush brows up & outward with a spoolie to see the natural shape",
      "If your product fills (pomade, pencil, pen, tinted gel): add short feathery strokes — never one long line",
      "If your product is a clear gel or styling wax: skip filling, just comb the wax through to lock the shape up",
      "Finish with the spoolie or wand combed upward to soften every stroke and lift the whole brow",
    ],
    render: () => (
      <>
        <Halo d="M66,112 Q82,98 98,110 Q82,114 66,114 Z" opacity={0.55} />
        <Halo d="M102,110 Q118,98 134,112 Q134,114 102,114 Z" opacity={0.55} />
        {/* Hair-stroke direction — left brow (upward outward) */}
        {[
          [70, 112, 73, 102], [74, 110, 77, 100], [78, 109, 81, 99],
          [82, 108, 85, 98], [86, 108, 89, 99], [90, 109, 93, 100],
          [94, 110, 96, 102],
        ].map(([x1, y1, x2, y2]) => (
          <HairStroke key={`lbrow-${x1}-${y1}`} from={[x1, y1]} to={[x2, y2]} />
        ))}
        {/* Hair-stroke direction — right brow */}
        {[
          [106, 110, 108, 102], [110, 109, 113, 100], [114, 108, 117, 99],
          [118, 108, 121, 98], [122, 109, 125, 99], [126, 110, 129, 100],
          [130, 112, 132, 102],
        ].map(([x1, y1, x2, y2]) => (
          <HairStroke key={`rbrow-${x1}-${y1}`} from={[x1, y1]} to={[x2, y2]} />
        ))}
        {/* Outward direction arrows along the brow */}
        <Arrow from={[68, 113]} to={[94, 110]} width={1.2} opacity={0.85} />
        <Arrow from={[132, 113]} to={[106, 110]} width={1.2} opacity={0.85} />
      </>
    ),
  },

  eyeshadow: {
    caption: "Sweep across the lid in a windshield-wiper motion — back, forth, blend",
    tool: "Soft fluffy eyeshadow brush",
    steps: [
      "Tap excess powder off the brush before touching your face",
      "Sweep across the lid back & forth like a windshield wiper",
      "Stop at the crease — don't go above the eye socket",
      "Use a clean fluffy brush in small circles to diffuse the edge",
    ],
    render: () => (
      <>
        <Halo d="M72,114 Q82,109 92,116 Q82,122 72,118 Z" opacity={0.65} />
        <Halo d="M108,116 Q118,109 128,114 Q128,118 108,122 Z" opacity={0.65} />
        {/* Back-and-forth sweep arcs */}
        <CurveArrow from={[74, 117]} ctrl={[82, 110]} to={[91, 117]} width={1.3} />
        <CurveArrow from={[91, 119]} ctrl={[82, 124]} to={[74, 119]} width={1.3} dashed />
        <CurveArrow from={[109, 117]} ctrl={[118, 110]} to={[126, 117]} width={1.3} />
        <CurveArrow from={[126, 119]} ctrl={[118, 124]} to={[109, 119]} width={1.3} dashed />
      </>
    ),
  },

  eyeliner: {
    caption: "Trace along the lash line from inner corner outward, then flick up at the tail",
    tool: "Liquid pen or felt-tip liner",
    steps: [
      "Look slightly down into a mirror so the lid lays flat",
      "Start at the inner corner with the thinnest line",
      "Trace along the upper lash line, thickening slightly toward the outer corner",
      "Flick up & out at the tail to lift the eye",
      "Touch up gaps with tiny dots, then connect them",
    ],
    render: () => (
      <>
        {/* Lash-line liner */}
        <path d="M75,119 Q82,117 91,120" stroke={COLORS.zone} strokeWidth="2.2" strokeLinecap="round" fill="none" />
        <path d="M109,120 Q118,117 125,119" stroke={COLORS.zone} strokeWidth="2.2" strokeLinecap="round" fill="none" />
        {/* Wing flicks */}
        <path d="M91,120 L96,117" stroke={COLORS.zone} strokeWidth="2" strokeLinecap="round" />
        <path d="M109,120 L104,117" stroke={COLORS.zone} strokeWidth="2" strokeLinecap="round" />
        {/* Direction arrows showing inner→outer trace */}
        <Arrow from={[76, 122]} to={[95, 117]} width={1.1} />
        <Arrow from={[124, 122]} to={[105, 117]} width={1.1} />
        {/* Inner-corner start dots */}
        <TapDot cx={75} cy={120} r={1} />
        <TapDot cx={125} cy={120} r={1} />
      </>
    ),
  },

  mascara: {
    caption: "Wiggle the wand from root to tip — a tiny zigzag separates every lash",
    tool: "Mascara wand",
    steps: [
      "Look down into a mirror so you can see the full lash",
      "Place the wand at the very base of the lashes",
      "Wiggle side-to-side as you sweep upward to the tips — this separates them",
      "Do a second coat only on the tips for length, not bottom-up (avoids clumps)",
    ],
    render: () => (
      <>
        {/* Lashes */}
        {[78, 82, 86, 90].map((x) => (
          <line key={`ll${x}`} x1={x} y1={119} x2={x - 1} y2={113} stroke={COLORS.zone} strokeWidth="1.2" strokeLinecap="round" />
        ))}
        {[110, 114, 118, 122].map((x) => (
          <line key={`rl${x}`} x1={x} y1={119} x2={x + 1} y2={113} stroke={COLORS.zone} strokeWidth="1.2" strokeLinecap="round" />
        ))}
        {/* Wiggle motion path on top of left lash bunch */}
        <Wiggle from={[84, 120]} to={[81, 108]} amp={1.6} segs={5} width={1.1} />
        <Wiggle from={[116, 120]} to={[119, 108]} amp={1.6} segs={5} width={1.1} />
        {/* Direction arrow tip */}
        <Arrow from={[84, 116]} to={[82, 108]} width={1} opacity={0.7} />
        <Arrow from={[116, 116]} to={[118, 108]} width={1} opacity={0.7} />
      </>
    ),
  },

  lip_liner: {
    caption: "Trace your natural lip line — over-draw very lightly to define the shape",
    tool: "Sharp lip pencil",
    steps: [
      "Start at the cupid's bow (the two peaks on the upper lip)",
      "Trace outward along the natural lip line in short, light strokes",
      "Optionally over-draw by 1mm where you want fuller shape",
      "Fill in the lips lightly with the liner so lipstick has something to grip",
    ],
    render: () => (
      <>
        <path
          d="M86,170 Q100,162 114,170 Q100,178 86,170 Z"
          fill="none" stroke={COLORS.zone} strokeWidth="1.6"
          strokeDasharray="2 1.5"
        />
        {/* Direction arrow following the curve */}
        <CurveArrow from={[100, 163]} ctrl={[110, 165]} to={[114, 170]} width={1.2} />
        <CurveArrow from={[114, 170]} ctrl={[108, 176]} to={[100, 178]} width={1.2} />
        <CurveArrow from={[100, 178]} ctrl={[92, 176]} to={[86, 170]} width={1.2} />
        <CurveArrow from={[86, 170]} ctrl={[90, 165]} to={[100, 163]} width={1.2} />
      </>
    ),
  },

  lipstick: {
    caption: "Fill from the center of the lip outward to each corner",
    tool: "Lipstick bullet or lip brush",
    steps: [
      "Smile slightly to flatten out the lip surface",
      "Start in the center and fill outward toward the corners",
      "Press lips together once to even out the color",
      "Blot with a tissue if you want a softer, longer-wearing finish",
    ],
    render: () => (
      <>
        <path d="M86,170 Q100,162 114,170 Q100,178 86,170 Z" fill={COLORS.zone} opacity={0.65} />
        <path d="M86,170 Q100,162 114,170 Q100,178 86,170 Z" fill="none" stroke={COLORS.zone} strokeWidth="1" />
        {/* Outward fill arrows from center */}
        <Arrow from={[100, 168]} to={[88, 169]} width={1.1} />
        <Arrow from={[100, 168]} to={[112, 169]} width={1.1} />
        <Arrow from={[100, 173]} to={[88, 172]} width={1.1} />
        <Arrow from={[100, 173]} to={[112, 172]} width={1.1} />
      </>
    ),
  },

  lip_gloss: {
    caption: "Dab a single dot in the center of the lip — let it spread for a full pout",
    tool: "Gloss wand",
    steps: [
      "Dot a small amount in the center of the bottom lip",
      "Press lips together once to spread it evenly",
      "Add a second dot to the cupid's bow for a 3D pout effect",
      "Skip the corners — gloss bleeds there and feels sticky",
    ],
    render: () => (
      <>
        <path d="M86,170 Q100,162 114,170 Q100,178 86,170 Z" fill={COLORS.zoneSoft} opacity={0.55} />
        <ellipse cx="100" cy="170" rx="6" ry="1.4" fill="#FFFFFF" opacity="0.95" />
        {/* Center dab dot */}
        <TapDot cx={100} cy={170} r={1.6} />
        {/* Spread arrows */}
        <Arrow from={[100, 170]} to={[92, 170]} width={1} opacity={0.7} />
        <Arrow from={[100, 170]} to={[108, 170]} width={1} opacity={0.7} />
        <Sparkle cx={100} cy={170} size={2.2} />
      </>
    ),
  },

  setting_powder: {
    caption: "Press (don't sweep) lightly on the T-zone — keeps it set without going matte",
    tool: "Big fluffy powder brush or velour puff",
    steps: [
      "Tap a tiny amount of powder onto the brush — less is more",
      "Press (don't sweep) onto the T-zone: forehead, nose, chin",
      "Dust very lightly under the eyes to set concealer",
      "Skip cheeks if you want to keep that lit-from-within glow",
    ],
    render: () => (
      <>
        <Halo d={FACE_PATH} opacity={0.25} />
        {/* T-zone — forehead band + nose */}
        <Halo d="M70,84 Q100,76 130,84 Q132,98 100,100 Q68,98 70,84 Z" opacity={0.55} />
        <Halo d="M96,100 Q100,98 104,100 Q104,150 100,154 Q96,150 96,100 Z" opacity={0.5} />
        {/* Press dots on the T */}
        {[
          [82, 90], [92, 86], [100, 84], [108, 86], [118, 90],
          [100, 110], [100, 124], [100, 140],
        ].map(([x, y]) => <TapDot key={`sp-${x}-${y}`} cx={x} cy={y} r={1.2} />)}
      </>
    ),
  },
};

const FALLBACK = {
  caption: "Apply where it feels right — soft, blended",
  steps: ["Pick the spot that feels right", "Apply softly", "Blend until it disappears"],
  tool: "Whatever you have on hand",
  render: () => <Halo d={FACE_PATH} opacity={0.3} />,
};

export const getTechniqueCaption = (category) =>
  (TECHNIQUES[category] || FALLBACK).caption;

/**
 * Returns a numbered, plain-language motion guide for the given category.
 * 3-5 short imperatives the user can follow without any visual aid.
 * Used by Routine.jsx alongside the SVG schematic for clearer guidance.
 */
export const getTechniqueMotionGuide = (category) => {
  const t = TECHNIQUES[category] || FALLBACK;
  return {
    caption: t.caption,
    steps: t.steps || [t.caption],
    tool: t.tool || "Soft brush or fingertips",
  };
};

const TechniqueSchematic = ({ category, label, className = "" }) => {
  const technique = TECHNIQUES[category] || FALLBACK;

  return (
    <div className={`relative ${className}`}>
      <svg
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-full"
        role="img"
        aria-label={`Technique schematic for ${label || category}: ${technique.caption}`}
        data-testid={`technique-schematic-${category}`}
      >
        <defs>
          <filter id="zone-blur" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="2" />
          </filter>
          <marker
            id="arrow-head"
            markerWidth="6"
            markerHeight="6"
            refX="5"
            refY="3"
            orient="auto"
          >
            <path d="M0,0 L6,3 L0,6 Z" fill={COLORS.arrow} />
          </marker>
          <radialGradient id="bg-glow" cx="50%" cy="50%" r="60%">
            <stop offset="0%" stopColor="#FFF6F2" stopOpacity="1" />
            <stop offset="100%" stopColor="#F8EEEE" stopOpacity="0" />
          </radialGradient>
        </defs>

        <rect width={VB_W} height={VB_H} fill="url(#bg-glow)" />

        <FaceBase />
        {technique.render()}
      </svg>
    </div>
  );
};

export default TechniqueSchematic;
